import { Component, DoCheck, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { BaseComponent, FSP_COMMUNITY_STRING_ID, HelperService, UserService, avatarColorFor, htmlFragmentToText, initialsFor, normalizeReactions } from "@mypxplat/xplat/core";
import { AppService, CommunityReactionService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { ReactionChangeEvent } from "../../reaction-control/reaction-control.component";
import { SimpleSignupComponent } from "../../modals/simple-signup/simple-signup.component";

/**
 * Compact feed row for a community post — the "list view" half of the view toggle
 * (DENG-848-adjacent; requested alongside the signature work).
 *
 * Deliberately a port of the MOBILE card (`ns-myfender`'s `fui-post-card`), not a
 * shrunken `<myp-comment>`: avatar, author, relative timestamp, subject, a three-line
 * body clamp, an optional media thumb, then a meta row of reply count, pin state and
 * reaction chips. Sizes and colours below are lifted from
 * `fui-post-card.component.scss` / `fui-reactions.component.scss` so the two platforms
 * read as the same list.
 *
 * The avatar/subject/body block is one link into the post detail. Reactions are live and
 * write through `CommunityReactionService`, the same path the detailed view uses. The
 * bookmark on a saved post is a button that unsaves it; saving and replying stay on the
 * detail view.
 */
/** A chip under a list row: the post's community or one of its channels. */
export interface PostListTag {
  kind: "community" | "channel";
  label: string;
  communityStringId: string;
  channelStringId?: string;
  /** The community's accent, for its chip's dot. */
  accent?: string | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community-post-list-item",
  templateUrl: "post-list-item.component.html",
  styleUrls: ["post-list-item.component.scss"],
  standalone: false,
})
export class PostListItemComponent extends BaseComponent implements DoCheck {
  @Input() communityStringId: string = FSP_COMMUNITY_STRING_ID;
  /**
   * Replies for this post, straight from `activeParentThreadMap`. Drives the reply count and
   * the row's timestamp.
   *
   * A setter because replies arrive AFTER the post does — the feed paints rows first and the
   * thread map fills in behind it. A plain `@Input` would leave the timestamp showing the
   * post's own time forever on a thread that is actually alive.
   */
  @Input() set replies(value: Array<any>) {
    this._replies = value;
    this._hydrateActivity();
  }
  get replies(): Array<any> {
    return this._replies;
  }
  private _replies: Array<any>;

  /**
   * When the thread was last active — its newest reply, or the post itself if nobody has
   * replied. This is what the row's timestamp shows: in a feed you scan to find what is
   * worth reading, "last touched" is the useful number and "posted on" is trivia.
   */
  public activityDate: Date | null = null;
  /**
   * Whether `activityDate` is actually a reply rather than the post. Only then is the row
   * labelled — on a thread with no replies the two are the same date, and "Latest activity"
   * in front of it would be noise on the majority of rows.
   */
  public hasLaterActivity = false;

  /**
   * The feed is showing posts beside the list rather than full-width. Presentation only —
   * opening is the shell's job either way (see `open`).
   */
  @Input() splitPaneActive = false;
  /**
   * Row is too narrow to carry reaction pills alongside the reply count. They are dropped
   * rather than wrapped — the detail pane shows the full set the moment the row is opened.
   */
  @Input() compact = false;
  /**
   * Where the post lives: its community (when the feed spans several) and its channels, as
   * chips under the text — what the mobile card's channel tags show. Labels come resolved
   * from the feed, which knows the channel lists; empty shows nothing.
   */
  @Input() tags: PostListTag[] = [];
  /** Chips are buttons that switch the feed (`tagClicked`); otherwise plain labels. */
  @Input() tagsInteractive = false;
  /**
   * The feed shows this row as unread (`li.community-post-unread`, a dot drawn in CSS). The dot
   * has no text, so the link carries a visually-hidden "Unread" for screen readers.
   */
  @Input() unread = false;
  /** This row's post is the one open in the pane: `aria-current` on its link. */
  @Input() current = false;
  @Output() tagClicked: EventEmitter<PostListTag> = new EventEmitter();
  /** Asks the feed to open this post — in its pane, or full-width in place of the list. */
  @Output() openPost: EventEmitter<string> = new EventEmitter();

  public authorName = "";
  public authorPhoto = "";
  public initials = "?";
  public avatarColor = "";
  public subject = "";
  public bodyPreview = "";
  /**
   * Search-highlighted markup, when the feed came from Algolia. Null otherwise, and the
   * plain stripped text above is used instead.
   *
   * Rendered through `[innerHTML]` WITHOUT the `safe` pipe, so Angular's sanitizer still
   * runs — it keeps `<span class="algolia-highlight">` and drops anything else. Post content
   * is only ever rendered as markup on this path; with no active search a row stays plain
   * text, so a post can't restyle the feed.
   */
  public subjectHtml: string | null = null;
  /**
   * Rows with a subject lead with it: subject, then a byline (small avatar, author, time),
   * then the body. Rows without one keep the avatar-left layout. Same split as the post
   * detail's `.subject-first` header.
   */
  public hasSubject = false;
  public bodyHtml: string | null = null;
  public thumbUrl = "";
  /** Non-image attachments still earn a tile; the glyph says which kind. */
  public thumbIcon: "play" | "video" | "file" | null = null;
  public isPinned = false;
  /** Which of the two `isPinned` covers: a moderator pin (thumbtack) rather than the reader's own save (bookmark). */
  public isAdminPinned = false;
  /** The reader saved this post (`pins`). Only this half of `isPinned` is clickable — it unsaves. */
  public isSaved = false;
  /**
   * Pre-resolved so the template binds a plain property. Live feed posts carry a Firestore
   * Timestamp; optimistic and queued ones carry a plain Date. Calling `.toDate()` from the
   * binding would re-run on every change-detection pass (the project's no-method-calls rule).
   */
  public createdDate: Date | null = null;
  public detailLink: any[] = [];
  /** The same destination as `detailLink`, as an href the anchor can carry. */
  public detailHref = "";

  private _post: any;
  /**
   * The post, for template reads of fields the feed keeps live on it — `replyCount` is
   * refreshed in place by `sortActiveParentPosts` whenever a reply or watcher update lands.
   */
  public postModel: any;

  @Input() set post(value: any) {
    this._post = value;
    this.postModel = value;
    this._hydrate();
  }
  get post(): any {
    return this._post;
  }

  constructor(
    userService: UserService,
    private _router: Router,
    private _helperService: HelperService,
    private _appService: AppService,
    private _reactionService: CommunityReactionService,
    public communityService: WebCommunityService
  ) {
    super(userService);

    // Saving in the detail pane writes to the pane's copy of the post, and the feed watcher
    // skips local writes — so without this the bookmark here only appeared after a reload.
    this.communityService.postPinsChanged$.pipe(takeUntilDestroyed()).subscribe(({ id, pins }) => {
      if (!this._post || this._post.id !== id) return;
      this._post.pins = pins;
      this._hydratePinned(this._post);
    });
  }

  /** Bookmark clicked: unsave. The row drops its bookmark at once; the service reverts on failure. */
  unsave(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.user || !this.isSaved) return;
    this.communityService.pinUnpinPost(this._post, this.env.communityFirestoreCollection).catch((error) => {
      console.error("Error unsaving post:", error);
    });
  }

  /**
   * Open the post — always by asking the shell, never by routing.
   *
   * Both of the shell's renderings (the pane, and the full-width detail on a narrow
   * viewport) keep the feed mounted, so there is no back-cache to prime and nothing to
   * restore: the list is still there underneath. Navigating instead would tear the shell
   * down and make closing a refetch, which is what this row used to do.
   */
  /** A chip was clicked: the feed decides what that means. Never opens the post. */
  onTag(tag: PostListTag, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.tagClicked.emit(tag);
  }

  open(event?: MouseEvent) {
    // Cmd/ctrl/shift-click and middle-click mean "somewhere else" — leave them to the
    // browser, which is the whole reason this is a real anchor.
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) return;
    event?.preventDefault();
    this.openPost.emit(String(this._post?.id));
  }

  /**
   * A reaction was picked on this row. Same rules as the detailed view, via the same
   * service: guests get the signup prompt, members who fail the community's participation
   * gate get their optimistic change reverted and an explanation, everyone else writes.
   */
  onReactionChange(event: ReactionChangeEvent) {
    const { comment, selectedType, previousTypes, nextTypes } = event;

    if (!this.user) {
      this._appService.promptSimpleSignup(SimpleSignupComponent).then(
        () => {},
        () => {}
      );
      this._reactionService.revert(comment, previousTypes);
      this._hydrateReactions(comment);
      return;
    }

    if (!this._reactionService.canReact(this.communityStringId)) {
      this._reactionService.revert(comment, previousTypes);
      this._hydrateReactions(comment);
      this._reactionService.explainBlock(this._reactionService.gateFor(this.communityStringId), this.communityStringId);
      return;
    }

    this._reactionService.persist({
      post: comment,
      selectedType,
      previousTypes,
      nextTypes,
      communityStringId: this.communityStringId,
      // A list row is always a top-level post in a regular community, so there is no parent
      // to point the notification at and no beta program to scope it to.
      channelName: Array.isArray(comment.channels) ? comment.channels.join(",") : comment.channels,
    });
    this._hydrateReactions(comment);
  }

  /** A reactor bubble in the control was clicked. */
  onViewProfile(userId: string) {
    if (!userId) return;
    this._router.navigate(["/profile", userId]);
  }

  /**
   * Resolve `activityDate` from the post, its stored `last_reply_at` (the feed usually skips
   * loading replies for list rows — see `deferRepliesInFeed`), and whatever replies are known.
   */
  /** What `activityDate` was last computed from — see `ngDoCheck`. */
  private _activityInputs: { lastReplyAt: any; replyCount: number } = { lastReplyAt: undefined, replyCount: -1 };

  /**
   * Live updates change the post IN PLACE: the parent's watcher writes the new
   * `last_reply_at` onto the same object, and a new reply is pushed onto the same array. No
   * input is reassigned, so the setters never fire and the row kept its old date. Two
   * reference/length comparisons per check — cheap, and it only recomputes on a real change.
   */
  ngDoCheck() {
    const lastReplyAt = this._post?.last_reply_at;
    const replyCount = this._replies?.length ?? 0;
    if (lastReplyAt !== this._activityInputs.lastReplyAt || replyCount !== this._activityInputs.replyCount) this._hydrateActivity();
  }

  private _hydrateActivity() {
    this._activityInputs = { lastReplyAt: this._post?.last_reply_at, replyCount: this._replies?.length ?? 0 };
    const created = this.createdDate;
    let latest = created;
    const lastReply = this._post?.last_reply_at;
    const lastReplyAt = lastReply?.toDate ? lastReply.toDate() : lastReply instanceof Date ? lastReply : null;
    if (lastReplyAt && (!latest || lastReplyAt.getTime() > latest.getTime())) latest = lastReplyAt;
    (this._replies || []).forEach((reply: any) => {
      const at = reply?.created?.toDate ? reply.created.toDate() : reply?.created instanceof Date ? reply.created : null;
      if (at && (!latest || at.getTime() > latest.getTime())) latest = at;
    });
    this.activityDate = latest || null;
    this.hasLaterActivity = !!(created && latest && latest.getTime() > created.getTime());
  }

  private _hydrate() {
    const post = this._post;
    if (!post) return;

    const author = post.author || {};
    this.authorName = author.name || "";
    this.authorPhoto = author.photo || "";
    // Shared helpers, so a member's tint here matches their post avatar, their reactor
    // bubble and the mobile card — all four hash the same id through the same palette.
    this.initials = initialsFor(this.authorName);
    this.avatarColor = avatarColorFor(author.id);

    // Subject and body are stored as HTML. Strip rather than render: a list row is a
    // one-line summary, and letting post markup into it would let a post restyle the feed.
    //
    // Search is the exception. The Algolia mapping in `channel.component` REPLACES
    // `post.subject` with highlighted markup (stashing the original on `_rawSubject`) and
    // puts a highlighted body extract on `highlightSnippet`. Strip from the original so the
    // plain fallback never shows markup, and keep the highlighted forms for rendering.
    const rawSubject = post._rawSubject ?? post.subject;
    // A text binding, so the entities the subject is stored with ("&amp;") have to be decoded
    // here — stripHtml only removes tags. See htmlFragmentToText.
    this.subject = htmlFragmentToText(rawSubject);
    this.subjectHtml = post._rawSubject ? post.subject : null;
    this.hasSubject = !!(this.subjectHtml || this.subject);
    this.bodyPreview = post.noHtmlMessageFlat || this._helperService.stripHtml(post.message || "", false, true) || "";
    this.bodyHtml = post.highlightSnippet || null;

    this._hydratePinned(post);
    this.createdDate = post.created?.toDate ? post.created.toDate() : post.created || null;
    this._hydrateActivity();

    this._hydrateThumb(post);
    this._hydrateReactions(post);

    const stringId = post.community_string_id || this.communityStringId || FSP_COMMUNITY_STRING_ID;
    this.detailLink = ["/community", stringId, "post", post.id];
    this.detailHref = `/community/${stringId}/post/${post.id}`;
  }

  private _hydratePinned(post: any) {
    this.isAdminPinned = !!post.pinned;
    this.isSaved = !!(post.pins && this.user && post.pins.includes(this.user.id));
    this.isPinned = this.isAdminPinned || this.isSaved;
  }

  private _hydrateThumb(post: any) {
    this.thumbUrl = "";
    this.thumbIcon = null;
    const file = post.files?.[0];
    if (file) {
      const ext = String(file.extension || "").toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(ext)) {
        this.thumbUrl = file.url || "";
        return;
      }
      this.thumbIcon = ["wav", "mp3", "aiff", "aif", "m4a", "flac", "aac", "ogg"].includes(ext) ? "play" : "file";
      return;
    }
    // A pasted YouTube/Vimeo link renders as an embed in the detail view; the list shows
    // the same video tile the mobile card does rather than a bare URL.
    if (post.embeds?.length) this.thumbIcon = "video";
  }

  /**
   * `<myp-reaction-control>` reads and optimistically mutates `post.reactions` directly, so
   * the normalised map has to live ON the post — not in a copy held here. Normalising is
   * also what folds legacy `likes[]` posts into the reactions shape the control expects.
   */
  private _hydrateReactions(post: any) {
    post.reactions = normalizeReactions(post);
  }
}
